import type { Id } from '@vp/shared-types';

export type LockState = 'acquiring' | 'owner' | 'readonly' | 'released';

interface LockMessage {
  type: 'takeover-request' | 'takeover-ready' | 'saved' | 'part-changed';
  canvasId: Id;
  from: string;
  to?: string;
  /** `part-changed`: which canvas part (canvas-parts.ts) and, when known, which item of it. */
  part?: string;
  key?: string;
}

/**
 * Single-writer lock for a canvas across browser tabs (§6.6), via Web Locks.
 * A second tab opens read-only; "take over" asks the owner to flush and release,
 * with a forced steal as fallback. Locks are released automatically when a tab dies.
 */
export class CanvasLock {
  private state: LockState = 'acquiring';
  private release: (() => void) | null = null;
  private channel: BroadcastChannel | null = null;
  private readonly tabId = Math.random().toString(36).slice(2);
  private listeners = new Set<(s: LockState) => void>();
  private savedListeners = new Set<() => void>();
  private partListeners = new Set<(part: string, key?: string) => void>();
  private abort: AbortController | null = null;

  constructor(
    readonly canvasId: Id,
    /** Called before handing the lock to another tab: flush unsaved changes. */
    private readonly beforeRelease: () => Promise<void>,
  ) {
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel('vp-canvas-lock');
      this.channel.onmessage = (e: MessageEvent<LockMessage>) => void this.onMessage(e.data);
    }
  }

  get current(): LockState {
    return this.state;
  }

  static get supported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.locks;
  }

  onChange(fn: (s: LockState) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Fired when the owner tab saved (read-only tabs refresh). */
  onRemoteSaved(fn: () => void): () => void {
    this.savedListeners.add(fn);
    return () => this.savedListeners.delete(fn);
  }

  /**
   * A part of the canvas (canvas-parts.ts: modules' data, data sets later) changed in another tab. Parts
   * are written outside transactions, so the "saved" refresh of objects does not cover them.
   */
  onRemotePartChanged(fn: (part: string, key?: string) => void): () => void {
    this.partListeners.add(fn);
    return () => this.partListeners.delete(fn);
  }

  /** Tell the other tabs of this canvas that one of its parts changed here. */
  announcePartChanged(part: string, key?: string): void {
    this.post({ type: 'part-changed', canvasId: this.canvasId, from: this.tabId, part, ...(key ? { key } : {}) });
  }

  private set(s: LockState): void {
    this.state = s;
    for (const fn of this.listeners) fn(s);
  }

  private get name(): string {
    return 'vp-canvas:' + this.canvasId;
  }

  /** Try to become the writer; resolves with the resulting state. */
  async acquire(): Promise<LockState> {
    if (!CanvasLock.supported) {
      this.set('owner');
      return 'owner';
    }
    const got = await this.request({ ifAvailable: true });
    if (!got) {
      this.set('readonly');
      // become owner automatically when the other tab closes
      void this.request({}).then((ok) => {
        if (ok) this.set('owner');
      });
    }
    return this.state;
  }

  private request(opts: LockOptions): Promise<boolean> {
    return new Promise<boolean>((resolveAcquire) => {
      this.abort?.abort();
      const ac = new AbortController();
      this.abort = ac;
      const options: LockOptions = { ...opts, mode: 'exclusive' };
      if (!opts.ifAvailable && !opts.steal) options.signal = ac.signal;
      navigator.locks
        .request(this.name, options, (lock) => {
          if (!lock) {
            resolveAcquire(false);
            return undefined;
          }
          this.set('owner');
          resolveAcquire(true);
          return new Promise<void>((release) => {
            this.release = release;
          });
        })
        .catch((e: unknown) => {
          // lock stolen by another tab, or our wait was aborted
          void e;
          if (this.state === 'owner') {
            this.release = null;
            this.set('readonly');
            // queue up to become the writer again once the other tab is gone
            void this.request({}).then((ok) => {
              if (ok) this.set('owner');
            });
          }
          resolveAcquire(false);
        });
    });
  }

  /** Ask the owner tab to hand over; falls back to stealing after a timeout. */
  async takeOver(timeoutMs = 1500): Promise<void> {
    if (!CanvasLock.supported || this.state === 'owner') return;
    this.abort?.abort();
    const ready = new Promise<void>((resolve) => {
      const handler = (e: MessageEvent<LockMessage>) => {
        if (e.data.type === 'takeover-ready' && e.data.canvasId === this.canvasId && e.data.to === this.tabId) {
          this.channel?.removeEventListener('message', handler);
          resolve();
        }
      };
      this.channel?.addEventListener('message', handler);
      setTimeout(resolve, timeoutMs);
    });
    this.post({ type: 'takeover-request', canvasId: this.canvasId, from: this.tabId });
    await ready;
    const got = await this.request({ ifAvailable: true });
    if (!got) await this.request({ steal: true });
  }

  private async onMessage(m: LockMessage): Promise<void> {
    if (m.canvasId !== this.canvasId || m.from === this.tabId) return;
    if (m.type === 'takeover-request' && this.state === 'owner') {
      try {
        await this.beforeRelease();
      } finally {
        this.release?.();
        this.release = null;
        this.set('readonly');
        this.post({ type: 'takeover-ready', canvasId: this.canvasId, from: this.tabId, to: m.from });
        // wait for the lock again (re-acquire when the other tab closes)
        void this.request({}).then((ok) => {
          if (ok) this.set('owner');
        });
      }
    } else if (m.type === 'saved' && this.state !== 'owner') {
      for (const fn of this.savedListeners) fn();
    } else if (m.type === 'part-changed' && m.part) {
      for (const fn of this.partListeners) fn(m.part, m.key);
    }
  }

  /** Owner announces a saved state so read-only tabs can refresh. */
  announceSaved(): void {
    if (this.state === 'owner') this.post({ type: 'saved', canvasId: this.canvasId, from: this.tabId });
  }

  private post(m: LockMessage): void {
    this.channel?.postMessage(m);
  }

  dispose(): void {
    this.abort?.abort();
    this.release?.();
    this.release = null;
    this.channel?.close();
    this.channel = null;
    this.set('released');
    this.listeners.clear();
    this.savedListeners.clear();
  }
}
